import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Upload, Loader2, FileBox, Trash2, CheckCircle2, XCircle, Clock, AlertTriangle, Eye, Sparkles, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCEPT = ".stl,.ply,.obj,.gcode,.g,.gco,.3mf,.dxd,.constructionInfo,.dme,.dmd,.zip";

/**
 * DesignApprovalPanel — finished-design upload + approval UI.
 *
 * Two modes, picked from `role`:
 *   - 'tech'   → file dropzone + delete buttons. Approval buttons disabled.
 *   - 'clinic' → big approval banner. Cannot upload, cannot delete.
 *
 * `onView(file)` is a callback the parent uses to open the existing STLViewer
 * for the chosen design file. Optional — if absent, the panel falls back to
 * the standard /api/files/{id} download link.
 *
 * `onCaseRefresh()` re-fetches the case so the parent's banner state stays
 * in sync after an upload / approve / reject.
 */
export default function DesignApprovalPanel({ case: caseDoc, role, onView, onCaseRefresh }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [deciding, setDeciding] = useState(null); // 'approve' | 'reject' | 'self_approve' | null
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [showSelfApproveModal, setShowSelfApproveModal] = useState(false);
    const [selfApproveReason, setSelfApproveReason] = useState("");
    const [selfApproveAck, setSelfApproveAck] = useState(false);
    // Re-evaluate "is the 2h hold elapsed?" every minute so the override
    // button appears the moment the case becomes eligible (no manual reload).
    const [_tick, setTick] = useState(0);
    useEffect(() => {
        if (!caseDoc?.design_uploaded_at) return undefined;
        const t = setInterval(() => setTick((n) => n + 1), 60_000);
        return () => clearInterval(t);
    }, [caseDoc?.design_uploaded_at]);

    const caseId = caseDoc?.id;
    const status = caseDoc?.design_approval_status || "none";
    const files = caseDoc?.design_files || [];
    const isTech = role === "tech";
    const isClinic = role === "clinic";

    // Self-approval eligibility. Default hold is 2h; we mirror the backend.
    const HOLD_HOURS = 2;
    const uploadedAt = caseDoc?.design_uploaded_at ? new Date(caseDoc.design_uploaded_at) : null;
    const hoursSinceUpload = uploadedAt ? (Date.now() - uploadedAt.getTime()) / 3_600_000 : 0;
    const remainingHoldHours = uploadedAt ? Math.max(0, HOLD_HOURS - hoursSinceUpload) : HOLD_HOURS;
    const selfApprovalEligible = isTech && (status === "pending" || status === "rejected") && hoursSinceUpload >= HOLD_HOURS;
    const selfApprovalCountdown = isTech && status === "pending" && hoursSinceUpload < HOLD_HOURS;

    const onPick = () => inputRef.current?.click();

    const onUpload = async (e) => {
        const fl = e.target.files;
        if (!fl || fl.length === 0) return;
        setUploading(true);
        try {
            for (const f of Array.from(fl)) {
                const form = new FormData();
                form.append("file", f);
                await axios.post(`${API}/admin/cases/${caseId}/design-files`, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            toast.success(`${fl.length} design file${fl.length > 1 ? "s" : ""} uploaded · clinic notified for approval`);
            onCaseRefresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.response?.data?.detail?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onDelete = async (fileId) => {
        if (!window.confirm("Remove this design file? The clinic will need to review the next upload.")) return;
        try {
            await axios.delete(`${API}/admin/cases/${caseId}/design-files/${fileId}`);
            toast.success("Design file removed");
            onCaseRefresh?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.detail || "Could not remove file");
        }
    };

    const onApprove = async () => {
        setDeciding("approve");
        try {
            await axios.post(`${API}/cases/${caseId}/design/approve`, {});
            toast.success("Design approved · lab can now start printing");
            onCaseRefresh?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.detail || "Could not approve");
        } finally { setDeciding(null); }
    };

    const onReject = async () => {
        if (rejectReason.trim().length < 3) {
            toast.error("Please share a brief note so the technician knows what to change.");
            return;
        }
        setDeciding("reject");
        try {
            await axios.post(`${API}/cases/${caseId}/design/reject`, { reason: rejectReason.trim() });
            toast.success("Design rejected · technician notified");
            setShowRejectModal(false);
            setRejectReason("");
            onCaseRefresh?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.detail || "Could not reject");
        } finally { setDeciding(null); }
    };

    const onSelfApprove = async () => {
        if (selfApproveReason.trim().length < 10) {
            toast.error("Please write at least 10 characters explaining why you're overriding the clinic.");
            return;
        }
        if (!selfApproveAck) {
            toast.error("You must tick the liability acknowledgement.");
            return;
        }
        setDeciding("self_approve");
        try {
            await axios.post(`${API}/cases/${caseId}/design/self-approve`, {
                reason: selfApproveReason.trim(),
                acknowledge_liability: true,
            });
            toast.success("Self-approved · clinic notified · audit-log row written");
            setShowSelfApproveModal(false);
            setSelfApproveReason("");
            setSelfApproveAck(false);
            onCaseRefresh?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            const msg = typeof d === "string" ? d : (d?.detail || "Could not self-approve");
            toast.error(msg);
        } finally { setDeciding(null); }
    };

    // -----------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------

    return (
        <section data-testid="design-approval-panel" className="rounded-2xl border border-slate-200 bg-white p-5 lg:p-6">
            <header className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center"><Sparkles size={17} /></div>
                    <div>
                        <div className="tr-overline">· Finished design</div>
                        <div className="font-display text-xl text-slate-900 tracking-tight">Approval workflow</div>
                    </div>
                </div>
                <StatusBadge status={status} />
            </header>

            {/* Clinic-side banners */}
            {isClinic && status === "pending" && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 flex items-start gap-3" data-testid="design-pending-banner">
                    <AlertTriangle className="text-amber-700 mt-0.5 shrink-0" size={18} />
                    <div className="text-[13.5px] text-amber-900">
                        <div className="font-semibold">Your technician uploaded a finished design.</div>
                        <div className="opacity-85 mt-0.5">Printing will start as soon as you approve. If the design needs changes, reject with a brief note and we'll re-upload.</div>
                    </div>
                </div>
            )}
            {isClinic && status === "approved" && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 mb-4 flex items-start gap-2.5" data-testid="design-approved-banner">
                    <CheckCircle2 className="text-emerald-700 shrink-0" size={17} />
                    <div className="text-[13px] text-emerald-900">
                        <span className="font-semibold">Approved.</span> The lab has started printing your case.
                    </div>
                </div>
            )}
            {isClinic && status === "rejected" && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mb-4 text-[13px] text-rose-900" data-testid="design-rejected-banner">
                    <div className="flex items-center gap-2 font-semibold"><XCircle size={16} /> You rejected this design.</div>
                    {caseDoc.design_rejection_reason && (
                        <div className="mt-1.5 text-rose-800/85">Note to lab: <em>"{caseDoc.design_rejection_reason}"</em></div>
                    )}
                    <div className="mt-1.5 opacity-75">The technician will upload a revised file shortly.</div>
                </div>
            )}

            {/* Tech-side: status hint */}
            {isTech && status === "rejected" && caseDoc.design_rejection_reason && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mb-4 text-[13px] text-rose-900">
                    <div className="font-semibold flex items-center gap-2"><AlertTriangle size={15} /> Clinic rejected — please revise.</div>
                    <div className="mt-1 italic">"{caseDoc.design_rejection_reason}"</div>
                </div>
            )}

            {/* File list */}
            {files.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-slate-200 px-5 py-10 text-center text-[13px] text-slate-500 mb-4">
                    {isTech ? "No finished design uploaded yet. Click \"Upload finished design\" below to attach a file." : "Your technician hasn't uploaded the finished design yet. We'll notify you when it's ready."}
                </div>
            ) : (
                <ul className="space-y-2 mb-4" data-testid="design-files-list">
                    {files.map((f) => (
                        <li key={f.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3 hover:border-cyan-300 transition" data-testid={`design-file-${f.id}`}>
                            <div className="h-9 w-9 rounded-lg bg-slate-50 text-slate-700 flex items-center justify-center shrink-0"><FileBox size={16} /></div>
                            <div className="flex-1 min-w-0">
                                <div className="font-mono-tr text-[12.5px] text-slate-900 truncate">{f.name}</div>
                                <div className="text-[11px] text-slate-500">
                                    {Math.max(1, Math.round((f.size || 0) / 1024)).toLocaleString()} KB · {(f.uploaded_at || "").slice(0, 16).replace("T", " ")} · by {f.uploaded_by || "—"}
                                </div>
                            </div>
                            {onView && [".stl", ".ply", ".obj"].includes((f.ext || "").toLowerCase()) && (
                                <button onClick={() => onView(f)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-[12px]" data-testid={`design-view-${f.id}`}>
                                    <Eye size={13} /> 3D
                                </button>
                            )}
                            {f.id && (
                                <a
                                    href={`${API}/files/${f.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-[12px]"
                                    data-testid={`design-download-${f.id}`}
                                >Download</a>
                            )}
                            {isTech && status !== "approved" && (
                                <button onClick={() => onDelete(f.id)} className="text-rose-500 hover:text-rose-700 p-1.5 rounded" data-testid={`design-delete-${f.id}`} aria-label="Delete">
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {/* Tech upload */}
            {isTech && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
                    <input ref={inputRef} type="file" accept={ACCEPT} multiple onChange={onUpload} className="hidden" data-testid="design-file-input" />
                    <button onClick={onPick} disabled={uploading} data-testid="design-upload-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0B1120] text-white hover:bg-slate-800 text-[13px] font-medium disabled:opacity-50 shrink-0 whitespace-nowrap">
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {files.length === 0 ? "Upload finished design" : "Upload revision"}
                    </button>
                    <div className="text-[11.5px] text-slate-500 leading-snug min-w-0 flex-1">
                        STL · PLY · OBJ · GCODE · 3MF · DXD · ZIP · 200 MB max · prefix <code className="bg-slate-100 px-1 rounded">tech_</code> added automatically
                    </div>
                </div>
            )}

            {/* Tech self-approval countdown — appears once design is uploaded but 2h hasn't elapsed yet */}
            {selfApprovalCountdown && (
                <div data-testid="self-approve-countdown" className="mt-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2.5 text-[12.5px] text-slate-600 flex items-start gap-2.5">
                    <Clock size={14} className="text-slate-500 mt-0.5 shrink-0" />
                    <div>
                        <strong className="text-slate-800">Self-approval available in ~{remainingHoldHours < 1 ? `${Math.ceil(remainingHoldHours * 60)} min` : `${remainingHoldHours.toFixed(1)} h`}.</strong>
                        {" "}If the clinic hasn't responded by then, you'll be able to override and proceed under your own liability.
                    </div>
                </div>
            )}

            {/* Tech self-approval button — appears after 2h with no clinic decision */}
            {selfApprovalEligible && (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="self-approve-cta">
                    <ShieldAlert className="text-amber-700 shrink-0" size={18} />
                    <div className="flex-1 min-w-[200px] text-[13px] text-amber-900 leading-snug">
                        <div className="font-semibold">Clinic hasn't responded in 2 h.</div>
                        <div className="text-amber-800/85">You can override the approval to keep the case moving. Liability for any post-delivery defect will shift to you.</div>
                    </div>
                    <button onClick={() => setShowSelfApproveModal(true)} disabled={deciding !== null}
                        data-testid="self-approve-open-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-[13px] font-semibold disabled:opacity-50">
                        <ShieldAlert size={14} /> Override · self-approve
                    </button>
                </div>
            )}

            {/* Self-approved banner (visible to all roles, distinguishes from clinic-approved) */}
            {status === "self_approved" && (
                <div data-testid="self-approved-banner" className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-[13px] text-amber-900">
                    <div className="flex items-center gap-2 font-semibold"><ShieldAlert size={15} /> Self-approved by technician</div>
                    {caseDoc.design_self_approval_reason && (
                        <div className="mt-1 text-amber-800/90">Note: <em>"{caseDoc.design_self_approval_reason}"</em></div>
                    )}
                    <div className="text-amber-800/70 mt-1 text-[12px]">
                        Manufacturing proceeded under technician liability after the 2 h clinic hold.
                    </div>
                </div>
            )}

            {/* Clinic actions */}
            {isClinic && status === "pending" && files.length > 0 && (
                <div className="mt-2 flex gap-2 pt-3 border-t border-slate-100">
                    <button onClick={onApprove} disabled={deciding !== null} data-testid="design-approve-btn"
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13.5px] font-semibold disabled:opacity-50">
                        {deciding === "approve" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        Approve · start printing
                    </button>
                    <button onClick={() => setShowRejectModal(true)} disabled={deciding !== null} data-testid="design-reject-btn"
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 text-[13.5px] font-medium disabled:opacity-50">
                        <XCircle size={14} /> Reject with note
                    </button>
                </div>
            )}

            {/* Reject reason modal */}
            {showRejectModal && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-5" data-testid="design-reject-modal">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto tr-modal-card">
                        <div className="tr-overline">· Reject design</div>
                        <div className="font-display text-xl text-slate-900 tracking-tight mt-1">Tell the technician what to change</div>
                        <p className="text-[13px] text-slate-500 mt-1.5">Be specific — e.g. <em>"contact point on #14 is too tight, please relieve mesial"</em>.</p>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={4}
                            className="w-full mt-3 rounded-lg border border-slate-300 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 px-3 py-2 text-[13.5px] outline-none"
                            placeholder="What needs to be adjusted?"
                            autoFocus
                            data-testid="design-reject-reason"
                        />
                        <div className="mt-4 flex gap-2">
                            <button onClick={() => { setShowRejectModal(false); setRejectReason(""); }}
                                className="flex-1 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-[13.5px]" data-testid="design-reject-cancel">
                                Cancel
                            </button>
                            <button onClick={onReject} disabled={deciding === "reject" || rejectReason.trim().length < 3}
                                className="flex-1 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[13.5px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                                data-testid="design-reject-confirm">
                                {deciding === "reject" ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                                Send rejection
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Self-approval modal */}
            {showSelfApproveModal && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-5" data-testid="self-approve-modal">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto tr-modal-card">
                        <div className="flex items-center gap-2 text-amber-700">
                            <ShieldAlert size={18} />
                            <div className="tr-overline text-amber-800">· Override clinic approval</div>
                        </div>
                        <div className="font-display text-xl text-slate-900 tracking-tight mt-1">You are taking responsibility for this case</div>
                        <p className="text-[13px] text-slate-600 mt-2 leading-relaxed">
                            Once you confirm, the case will move to <strong>manufacturing</strong> immediately under <strong>your liability</strong>. The clinic will be emailed and WhatsApp&apos;d. If a defect is reported after delivery, the remake cost shifts from the clinic to you.
                        </p>
                        <div className="mt-3">
                            <label htmlFor="self-approve-reason" className="text-[10.5px] font-mono-tr tracking-[0.16em] uppercase text-slate-500 block mb-1">
                                Why are you overriding? <span className="text-rose-600">*</span>
                            </label>
                            <textarea
                                id="self-approve-reason"
                                value={selfApproveReason}
                                onChange={(e) => setSelfApproveReason(e.target.value)}
                                rows={4}
                                minLength={10}
                                className="w-full rounded-lg border border-slate-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 px-3 py-2 text-[13.5px] outline-none"
                                placeholder="e.g. 'Clinic non-reachable since 8am. Margin fits within prescription tolerance, verified against last 2 approved cases.'"
                                autoFocus
                                data-testid="self-approve-reason"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">Minimum 10 characters. Stored permanently in the audit log.</div>
                        </div>
                        <label className="mt-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 cursor-pointer hover:bg-rose-100/60 transition">
                            <input
                                type="checkbox"
                                checked={selfApproveAck}
                                onChange={(e) => setSelfApproveAck(e.target.checked)}
                                data-testid="self-approve-ack"
                                className="mt-0.5 h-4 w-4 rounded border-rose-400 text-rose-700 focus:ring-rose-300"
                            />
                            <div className="text-[12.5px] text-rose-900 leading-snug">
                                I, <strong>{caseDoc?.design_uploaded_by || "the uploading technician"}</strong>, take full responsibility for any post-delivery error arising from this self-approved design. The clinic is not liable for remake costs on this case.
                            </div>
                        </label>
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => { setShowSelfApproveModal(false); setSelfApproveReason(""); setSelfApproveAck(false); }}
                                className="flex-1 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-[13.5px]" data-testid="self-approve-cancel">
                                Cancel
                            </button>
                            <button onClick={onSelfApprove} disabled={deciding === "self_approve" || !selfApproveAck || selfApproveReason.trim().length < 10}
                                className="flex-1 py-2.5 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-[13.5px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                                data-testid="self-approve-confirm">
                                {deciding === "self_approve" ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
                                Confirm override
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Timeline footer */}
            {(caseDoc.design_uploaded_at || caseDoc.design_approval_at) && (
                <div className="mt-4 pt-3 border-t border-slate-100 text-[11.5px] text-slate-500 space-y-0.5 font-mono-tr">
                    {caseDoc.design_uploaded_at && (
                        <div><Clock size={10} className="inline mr-1" /> {(caseDoc.design_uploaded_at || "").slice(0, 16).replace("T", " ")} · uploaded by {caseDoc.design_uploaded_by || "—"}</div>
                    )}
                    {caseDoc.design_approval_at && (
                        <div><Clock size={10} className="inline mr-1" /> {(caseDoc.design_approval_at || "").slice(0, 16).replace("T", " ")} · {status} by {caseDoc.design_approval_by || "—"}</div>
                    )}
                </div>
            )}
        </section>
    );
}

function StatusBadge({ status }) {
    const cfg = {
        none: { bg: "bg-slate-100", fg: "text-slate-700", label: "not started", Icon: Clock },
        pending: { bg: "bg-amber-100", fg: "text-amber-800", label: "awaiting approval", Icon: AlertTriangle },
        approved: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "approved", Icon: CheckCircle2 },
        rejected: { bg: "bg-rose-100", fg: "text-rose-800", label: "rejected", Icon: XCircle },
        self_approved: { bg: "bg-amber-200", fg: "text-amber-900", label: "self-approved · tech liability", Icon: ShieldAlert },
    }[status] || { bg: "bg-slate-100", fg: "text-slate-700", label: status, Icon: Clock };
    const { Icon } = cfg;
    return (
        <span data-testid="design-status-badge" data-status={status} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.fg} text-[11.5px] font-semibold tracking-wide uppercase`}>
            <Icon size={12} /> {cfg.label}
        </span>
    );
}
